import { Alert, AlertProps, AlertTitle, Box, FormControl, Grid, Snackbar, TextField } from '@mui/material';
import React from 'react';
import CustomDataGrid from '../Common/CustomDataGrid';
import { GridDataSourceCacheDefault, GridColumnVisibilityModel, GridDataSourceCache, useGridApiRef, GridColDef, GridDataSource, GridGetRowsParams, GridGetRowsResponse } from '@mui/x-data-grid';
import { GridRowModesModel } from '@mui/x-data-grid';
import ErrorBoundary from '../Common/ErrorBoundary';

export default function Settings() {
    const apiRef = useGridApiRef();
    const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
    const [lowTTLCache, setLowTTLCache] = React.useState<GridDataSourceCache>(() => new GridDataSourceCacheDefault({ ttl: 1000 * 10 }));
    const [columnVisibilityModel, setColumnVisibilityModel] = React.useState<GridColumnVisibilityModel>({});
    const [navigate, setNavigate] = React.useState<boolean>(false);
    
    const [snackbar, setSnackbar] = React.useState<Pick<
        AlertProps,
        'children' | 'severity'
    > | null>(null);

    const handleCloseSnackbar = () => setSnackbar(null);

    React.useEffect(() => {
        
    }, [snackbar]);

    const [income_columns, setIncomeColumns] = React.useState<GridColDef[]>([
        {
            field: 'id',
            headerName: 'ID',
            renderHeader: (params) => {
                return <span style={{ fontWeight: 'bold' }}>{params.colDef.headerName as string}</span>;
            },
            width: 50,
            align: 'center',
            headerAlign: 'center',
            type: 'number',
        },
        {
            field: 'type',
            headerName: 'Type',
            renderHeader: (params) => {
                return <span style={{ fontWeight: 'bold' }}>{params.colDef.headerName as string}</span>;
            },
            width: 300,
            align: 'center',
            headerAlign: 'center',
            type: 'string',
        },
        {
            field: 'shared_revenue',
            headerName: 'Share Of Revenue (%)',
            renderHeader: (params) => {
                return <span style={{ fontWeight: 'bold' }}>{params.colDef.headerName as string}</span>;
            },
            width: 150,
            align: 'center',
            headerAlign: 'center',
            type: 'string',
            editable: true,
            renderCell: (params) => {
                return <span>{params.value ? parseFloat(params.value).toFixed(1) : ''}</span>;
            },
            renderEditCell: (params) => (
                <React.Fragment>
                    {/* <FormControl variant="standard"> */}
                        <TextField
                            variant="standard"
                            size="small"
                            fullWidth
                            value={params.value}
                            onChange={(e) => {
                                // Update the cell value in the DataGrid's internal state
                                params.api.setEditCellValue({ id: params.id, field: params.field, value: e.target.value });
                                if (parseFloat(e.target.value) > 100) {
                                    setSnackbar({ children: 'Share of revenue must be between 0 and 100', severity: 'error' });
                                } else if (isNaN(parseFloat(e.target.value))) {
                                    setSnackbar({ children: 'Share of revenue must be a number', severity: 'error' });
                                }else if (parseFloat(e.target.value) < 0) {
                                    setSnackbar({ children: 'Share of revenue must be greater than 0', severity: 'error' });
                                }
                                else{
                                    setSnackbar(null);
                                }
                            }}
                        />
                    {/* </FormControl> */}
                </React.Fragment>
            ),
        },
        {
            field: 'created_at',
            headerName: 'Created At',
            renderHeader: (params) => {
                return <span style={{ fontWeight: 'bold' }}>{params.colDef.headerName as string}</span>;
            },
            width: 100,
            align: 'center',
            headerAlign: 'center',
            type: 'string',
        },  
        {
            field: 'updated_at',
            headerName: 'Updated At',
            renderHeader: (params) => {
                return <span style={{ fontWeight: 'bold' }}>{params.colDef.headerName as string}</span>;
            },
            width: 100,
            align: 'center',
            headerAlign: 'center',
            type: 'string',
        }
    ]);

    const [channel_columns, setChannelColumns] = React.useState<GridColDef[]>([
        {
            field: 'id',
            headerName: 'ID',
            renderHeader: (params) => {
                return <span style={{ fontWeight: 'bold' }}>{params.colDef.headerName as string}</span>;
            },
            width: 50,
            align: 'center',
            headerAlign: 'center',
            type: 'number',
        },
        {
            field: 'channel_id',
            headerName: 'Channel ID',
            renderHeader: (params) => {
                return <span style={{ fontWeight: 'bold' }}>{params.colDef.headerName as string}</span>;
            },
            width: 100,
            align: 'center',
            headerAlign: 'center',
            type: 'number',
            editable: true,
        },
        {
            field: 'trigger1',
            headerName: 'Trigger 1',
            renderHeader: (params) => {
                return <span style={{ fontWeight: 'bold' }}>{params.colDef.headerName as string}</span>;
            },
            width: 100,
            align: 'center',
            headerAlign: 'center',
            type: 'number',
            editable: true,
        },
        {
            field: 'trigger2',
            headerName: 'Trigger 2',
            renderHeader: (params) => {
                return <span style={{ fontWeight: 'bold' }}>{params.colDef.headerName as string}</span>;
            },
            width: 100,
            align: 'center',
            headerAlign: 'center',
            type: 'number',
            editable: true,
        },
        {
            field: 'weighting',
            headerName: 'Weighting',
            renderHeader: (params) => {
                return <span style={{ fontWeight: 'bold' }}>{params.colDef.headerName as string}</span>;
            },
            width: 100,
            align: 'center',
            headerAlign: 'center',
            type: 'number',
            editable: true,
        },
        {
            field: 'description',
            headerName: 'Description',
            renderHeader: (params) => {
                return <span style={{ fontWeight: 'bold' }}>{params.colDef.headerName as string}</span>;
            },
            width: 200,
            align: 'center',
            headerAlign: 'center',
            type: 'string',
        },
        {
            field: 'long_description',
            headerName: 'Long Description',
            renderHeader: (params) => {
                return <span style={{ fontWeight: 'bold' }}>{params.colDef.headerName as string}</span>;
            },
            width: 400,
            align: 'center',
            headerAlign: 'center',
            type: 'string',
        },
        {
            field: 'created_at',
            headerName: 'Created At',
            renderHeader: (params) => {
                return <span style={{ fontWeight: 'bold' }}>{params.colDef.headerName as string}</span>;
            },
            width: 100,
            align: 'center',
            headerAlign: 'center',
            type: 'string'
        },
        {
            field: 'updated_at',
            headerName: 'Updated At',
            renderHeader: (params) => {
                return <span style={{ fontWeight: 'bold' }}>{params.colDef.headerName as string}</span>;
            },
            width: 100,
            align: 'center',
            headerAlign: 'center',
            type: 'string',
        }
    ]);

    const income_gridDataSource: GridDataSource = {
        getRows: async (params: GridGetRowsParams): Promise<GridGetRowsResponse> => {

            const urlParams = new URLSearchParams();
            // if (params.paginationModel.page > 0) {
            urlParams.set('paginationModel', JSON.stringify(params.paginationModel));
            // }
            if (params.sortModel.length > 0) {
                urlParams.set('sortModel', JSON.stringify(params.sortModel));
            }
            if (params.filterModel.items.length > 0) {
                urlParams.set('filterModel', JSON.stringify(params.filterModel));
            }

            const urlParamsString = urlParams.size > 0 ? `?${urlParams.toString()}&entity_id=${localStorage.getItem('entity_id')}` : `?entity_id=${localStorage.getItem('entity_id')}`;
            const response = await fetch(`/admin/settings/income-statement-estimates/${urlParamsString}`);
            const data = await response.json();
            return {
                rows: data.settings,
                rowCount: data.rowCount,
            };
        },
    }


    const channel_gridDataSource: GridDataSource = {
        getRows: async (params: GridGetRowsParams): Promise<GridGetRowsResponse> => {

            const urlParams = new URLSearchParams();
            urlParams.set('paginationModel', JSON.stringify(params.paginationModel));
            if (params.sortModel.length > 0) {
                urlParams.set('sortModel', JSON.stringify(params.sortModel));
            }
            if (params.filterModel.items.length > 0) {
                urlParams.set('filterModel', JSON.stringify(params.filterModel));
            }

            const urlParamsString = urlParams.size > 0 ? `?${urlParams.toString()}&entity_id=${localStorage.getItem('entity_id')}` : `?entity_id=${localStorage.getItem('entity_id')}`;
            const response = await fetch(`/admin/settings/channel-settings/${urlParamsString}`);
            const data = await response.json();
            return {
                rows: data.settings,
                rowCount: data.rowCount,
            };
        },
    }

    return (
        <ErrorBoundary fallback={<div>Error loading settings</div>}>
            <Box style={{ maxWidth: '100% !important', display: 'flex', flexDirection: 'row', gap: 2}}>
                <Grid container spacing={2} sx={{ width: '100%', height: '100%'}}>
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} sx={{ borderRadius: '10px', boxShadow: '0 0 2px #fff', border: 1, borderColor: 'divider', overflow: 'hidden' }}>
                        {!!snackbar && (
                            <Alert severity={snackbar?.severity as AlertProps['severity']} style={{ fontSize: 'clamp(0.75rem, 3vw, 0.8rem) !important;', marginBottom: 10 }}>
                                <AlertTitle>Error</AlertTitle>
                                {snackbar?.children as string}
                            </Alert>
                            // <Snackbar sx={{ position: 'absolute' }} open={!!snackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                            //     <Alert {...snackbar} onClose={handleCloseSnackbar} />
                            // </Snackbar>
                        )}
                        <CustomDataGrid
                            title="Income Statement Estimates"
                            url="/admin/settings/income-statement-estimates"
                            exportUrl="/admin/settings/income-statement-estimates/export/"
                            columns={income_columns}
                            sortModel={[]}
                            gridDataSource={income_gridDataSource}
                            apiRef={apiRef}
                            rowModesModel={rowModesModel}
                            lowTTLCache={lowTTLCache}
                            columnVisibilityModel={columnVisibilityModel}
                            navigate={navigate}
                            actions={['edit']}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} sx={{ borderRadius: '10px', boxShadow: '0 0 2px #fff', border: 1, borderColor: 'divider', overflow: 'hidden' }}>
                        <CustomDataGrid
                            title="Channel Settings"
                            url="/admin/settings/channel-settings"
                            exportUrl="/admin/settings/channel-settings/export/"
                            columns={channel_columns}
                            sortModel={[]}
                            gridDataSource={channel_gridDataSource}
                            apiRef={apiRef}
                            rowModesModel={rowModesModel}
                            lowTTLCache={lowTTLCache}
                            columnVisibilityModel={columnVisibilityModel}
                            navigate={navigate}
                            actions={['edit']}
                        />
                    </Grid>
                </Grid>
            </Box>
        </ErrorBoundary>
    );
}